// import React, { useState, FormEvent } from 'react';
// import { UpdateUserRequest, User } from '../types/user';
// import '../styles/EditUserForm.css';

// interface Props {
//     user: User;
//     onSubmit: (id: string, data: UpdateUserRequest) => Promise<void>;
//     onCancel: () => void;
//     allowedRoles: ('admin' | 'viewer')[];
//     isSelfEdit?: boolean;
// }

// export default function EditUserForm({
//                                          user,
//                                          onSubmit,
//                                          onCancel,
//                                          allowedRoles,
//                                          isSelfEdit = false,
//                                      }: Props) {
//     const [form, setForm] = useState<UpdateUserRequest>({
//         email: user.email,
//         username: user.username,
//         ...(isSelfEdit ? {} : {
//             role: allowedRoles.includes(user.role as any)
//                 ? (user.role as 'admin' | 'viewer')
//                 : allowedRoles[0]
//         }),
//     });
//     const [error, setError] = useState<string>('');

//     const handleChange = (
//         e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
//     ) => {
//         setForm({ ...form, [e.target.name]: e.target.value } as any);
//     };

//     const handleSubmit = async (e: FormEvent) => {
//         e.preventDefault();
//         setError('');
//         try {
//             await onSubmit(user.id, form);
//             onCancel();
//         } catch (err: any) {
//             setError(err.response?.data?.message || err.message);
//         }
//     };

//     return (
//         <div className="user-form-overlay">
//             <div className="user-form-container">
//                 <div className="user-form-header">
//                     <h1 className="user-form-title">Edit {user.username}</h1>
//                 </div>
//                 {error && <div className="user-form-error">{error}</div>}

//                 <form className="user-form-grid" onSubmit={handleSubmit}>
//                     <div className="user-form-group" style={{ gridColumn: '1/-1' }}>
//                         <label className="user-form-label" htmlFor="email">
//                             Email
//                         </label>
//                         <input
//                             id="email"
//                             name="email"
//                             type="email"
//                             className="user-form-input"
//                             value={form.email}
//                             onChange={handleChange}
//                             required
//                             placeholder="user@example.com"
//                             autoComplete="off"
//                         />
//                     </div>
//                     <div className="user-form-group">
//                         <label className="user-form-label" htmlFor="username">
//                             Username
//                         </label>
//                         <input
//                             id="username"
//                             name="username"
//                             type="text"
//                             className="user-form-input"
//                             value={form.username}
//                             onChange={handleChange}
//                             required
//                             minLength={3}
//                             placeholder="Username"
//                             autoComplete="off"
//                         />
//                     </div>
//                     {!isSelfEdit && (
//                         <div className="user-form-group">
//                             <label className="user-form-label" htmlFor="role">
//                                 Role
//                             </label>
//                             <select
//                                 id="role"
//                                 name="role"
//                                 className="user-form-input"
//                                 value={form.role}
//                                 onChange={handleChange}
//                                 required
//                             >
//                                 {allowedRoles.map((r) => (
//                                     <option key={r} value={r}>
//                                         {r.charAt(0).toUpperCase() + r.slice(1)}
//                                     </option>
//                                 ))}
//                             </select>
//                         </div>
//                     )}
//                     <div className="user-button-group" style={{ gridColumn: '1/-1' }}>
//                         <button
//                             type="button"
//                             className="user-btn user-btn-cancel"
//                             onClick={onCancel}
//                         >
//                             Cancel
//                         </button>
//                         <button
//                             type="submit"
//                             className="user-btn user-btn-save"
//                         >
//                             Save
//                         </button>
//                     </div>
//                 </form>
//             </div>
//         </div>
//     );
// }

import React, { FormEvent, useEffect, useId, useRef, useState } from "react";

import { AtSign, Mail, ShieldCheck, UserRoundCog, X } from "lucide-react";

import { UpdateUserRequest, User } from "../types/user";

interface Props {
  user: User;

  onSubmit: (id: string, data: UpdateUserRequest) => Promise<void>;

  onCancel: () => void;

  allowedRoles: ("admin" | "viewer")[];

  isSelfEdit?: boolean;
}

/* =========================================================
   COMPONENT
========================================================= */

export default function EditUserForm({
  user,
  onSubmit,
  onCancel,
  allowedRoles,
  isSelfEdit = false,
}: Props) {
  const [form, setForm] = useState<UpdateUserRequest>({
    email: user.email,

    username: user.username,

    ...(isSelfEdit
      ? {}
      : {
          role: allowedRoles.includes(user.role as any)
            ? (user.role as "admin" | "viewer")
            : allowedRoles[0],
        }),
  });

  const [error, setError] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);

  const titleId = useId();

  const descriptionId = useId();

  /* =====================================================
       MODAL BEHAVIOUR
    ===================================================== */

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    window.setTimeout(() => {
      emailRef.current?.focus();
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) {
        onCancel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onCancel, submitting]);

  /* =====================================================
       CHANGE
    ===================================================== */

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setForm((current) => ({
      ...current,

      [event.target.name]: event.target.value,
    }));

    if (error) {
      setError("");
    }
  };

  /* =====================================================
       SUBMIT
    ===================================================== */

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      await onSubmit(user.id, form);

      onCancel();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to update user.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =====================================================
       SHARED INPUT
    ===================================================== */

  const inputClass = `
        box-border
        h-10
        w-full
        rounded-[9px]
        border
        border-slate-200
        bg-white
        px-3
        text-[12px]
        text-slate-900
        outline-none
        transition
        placeholder:text-slate-400
        hover:border-slate-300
        focus:border-slate-400
        focus:ring-2
        focus:ring-slate-100
        disabled:cursor-not-allowed
        disabled:bg-slate-50
        disabled:text-slate-500
    `;

  /* =====================================================
       RENDER
    ===================================================== */

  return (
    <div
      role="presentation"
      className="
                fixed
                inset-0
                z-[1200]
                flex
                items-center
                justify-center
                bg-slate-950/45
                p-4
                backdrop-blur-[1px]
            "
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) {
          onCancel();
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="
                    flex
                    max-h-[calc(100vh-32px)]
                    w-full
                    max-w-[520px]
                    flex-col
                    overflow-hidden
                    rounded-[18px]
                    border
                    border-slate-200
                    bg-white
                    shadow-[0_24px_80px_rgba(15,23,42,0.25)]
                "
      >
        {/* =========================================
                    HEADER
                ========================================= */}

        <header
          className="
                        flex
                        shrink-0
                        items-start
                        justify-between
                        gap-4
                        border-b
                        border-slate-100
                        px-6
                        py-5

                        max-sm:px-4
                    "
        >
          <div
            className="
                            flex
                            min-w-0
                            items-start
                            gap-3
                        "
          >
            <div
              className="
                                grid
                                h-10
                                w-10
                                shrink-0
                                place-items-center
                                rounded-[11px]
                                bg-slate-100
                                text-slate-600
                            "
            >
              <UserRoundCog size={19} strokeWidth={1.8} />
            </div>

            <div className="min-w-0">
              <h2
                id={titleId}
                className="
                                    m-0
                                    truncate
                                    text-[18px]
                                    font-bold
                                    tracking-[-0.02em]
                                    text-slate-950
                                "
              >
                Edit User
              </h2>

              <p
                id={descriptionId}
                className="
                                    mb-0
                                    mt-1
                                    max-w-[330px]
                                    truncate
                                    text-[10px]
                                    text-slate-400
                                "
                title={user.username}
              >
                Update account details for @{user.username}.
              </p>
            </div>
          </div>

          <button
            type="button"
            title="Close"
            aria-label="Close"
            disabled={submitting}
            onClick={onCancel}
            className="
                            grid
                            h-8
                            w-8
                            shrink-0
                            place-items-center
                            rounded-lg
                            border-0
                            bg-transparent
                            p-0
                            text-slate-400
                            transition
                            hover:bg-slate-100
                            hover:text-slate-800
                            disabled:opacity-40
                        "
          >
            <X size={17} />
          </button>
        </header>

        {/* =========================================
                    FORM
                ========================================= */}

        <form
          onSubmit={handleSubmit}
          className="
                        flex
                        min-h-0
                        flex-1
                        flex-col
                    "
        >
          {/* BODY */}

          <div
            className="
                            min-h-0
                            flex-1
                            overflow-y-auto
                            px-6
                            py-5

                            max-sm:px-4
                        "
          >
            {error && (
              <div
                role="alert"
                className="
                                    mb-4
                                    rounded-[9px]
                                    border
                                    border-red-200
                                    bg-red-50
                                    px-3
                                    py-2.5
                                    text-[11px]
                                    font-medium
                                    text-red-700
                                "
              >
                {error}
              </div>
            )}

            <div
              className="
                                grid
                                grid-cols-2
                                gap-x-3
                                gap-y-4

                                max-sm:grid-cols-1
                            "
            >
              {/* EMAIL */}

              <label
                className="
                                    col-span-2
                                    block

                                    max-sm:col-span-1
                                "
              >
                <span
                  className="
                                        mb-1.5
                                        block
                                        text-[11px]
                                        font-semibold
                                        text-slate-700
                                    "
                >
                  Email
                  <span className="ml-1 text-red-500">*</span>
                </span>

                <div className="relative">
                  <Mail
                    size={15}
                    strokeWidth={1.8}
                    className="
                                            pointer-events-none
                                            absolute
                                            left-3
                                            top-1/2
                                            -translate-y-1/2
                                            text-slate-400
                                        "
                  />

                  <input
                    ref={emailRef}
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                    placeholder="user@example.com"
                    autoComplete="off"
                    className={`
                                            ${inputClass}
                                            pl-9
                                        `}
                  />
                </div>
              </label>

              {/* USERNAME */}

              <label
                className={
                  isSelfEdit ? "col-span-2 max-sm:col-span-1" : "block"
                }
              >
                <span
                  className="
                                        mb-1.5
                                        block
                                        text-[11px]
                                        font-semibold
                                        text-slate-700
                                    "
                >
                  Username
                  <span className="ml-1 text-red-500">*</span>
                </span>

                <div className="relative">
                  <AtSign
                    size={15}
                    strokeWidth={1.8}
                    className="
                                            pointer-events-none
                                            absolute
                                            left-3
                                            top-1/2
                                            -translate-y-1/2
                                            text-slate-400
                                        "
                  />

                  <input
                    id="username"
                    name="username"
                    type="text"
                    value={form.username}
                    onChange={handleChange}
                    required
                    minLength={3}
                    disabled={submitting}
                    placeholder="Username"
                    autoComplete="off"
                    className={`
                                            ${inputClass}
                                            pl-9
                                        `}
                  />
                </div>

                <span
                  className="
                                        mt-1
                                        block
                                        text-[9px]
                                        text-slate-400
                                    "
                >
                  Minimum 3 characters.
                </span>
              </label>

              {/* ROLE */}

              {!isSelfEdit && (
                <label className="block">
                  <span
                    className="
                                            mb-1.5
                                            block
                                            text-[11px]
                                            font-semibold
                                            text-slate-700
                                        "
                  >
                    Role
                    <span className="ml-1 text-red-500">*</span>
                  </span>

                  <div className="relative">
                    <ShieldCheck
                      size={15}
                      strokeWidth={1.8}
                      className="
                                                pointer-events-none
                                                absolute
                                                left-3
                                                top-1/2
                                                -translate-y-1/2
                                                text-slate-400
                                            "
                    />

                    <select
                      id="role"
                      name="role"
                      value={form.role}
                      onChange={handleChange}
                      required
                      disabled={submitting}
                      className={`
                                                ${inputClass}
                                                appearance-none
                                                pl-9 pr-9
                                            `}
                    >
                      {allowedRoles.map((role) => (
                        <option key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </option>
                      ))}
                    </select>

                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      className="
                                                pointer-events-none
                                                absolute
                                                right-3
                                                top-1/2
                                                h-4
                                                w-4
                                                -translate-y-1/2
                                                text-slate-400
                                            "
                    >
                      <path
                        d="m7 10 5 5 5-5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <span
                    className="
                                            mt-1
                                            block
                                            text-[9px]
                                            text-slate-400
                                        "
                  >
                    Controls system permissions.
                  </span>
                </label>
              )}
            </div>

            {/* SELF EDIT NOTICE */}

            {isSelfEdit && (
              <div
                className="
                                    mt-4
                                    rounded-[9px]
                                    border
                                    border-slate-200
                                    bg-slate-50
                                    px-3
                                    py-2.5
                                    text-[10px]
                                    leading-4
                                    text-slate-500
                                "
              >
                Your own role cannot be changed from this form.
              </div>
            )}
          </div>

          {/* =====================================
                        FOOTER
                    ===================================== */}

          <footer
            className="
                            flex
                            shrink-0
                            justify-end
                            gap-2
                            border-t
                            border-slate-100
                            px-6
                            py-4

                            max-sm:px-4
                        "
          >
            <button
              type="button"
              disabled={submitting}
              onClick={onCancel}
              className="
                                h-10
                                min-w-[100px]
                                rounded-[9px]
                                border
                                border-slate-200
                                bg-white
                                px-4
                                text-[11px]
                                font-semibold
                                text-slate-700
                                transition
                                hover:bg-slate-50
                                disabled:opacity-50

                                max-sm:flex-1
                            "
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="
                                inline-flex
                                h-10
                                min-w-[120px]
                                items-center
                                justify-center
                                gap-2
                                rounded-[9px]
                                border-0
                                bg-[#0d162a]
                                px-4
                                text-[11px]
                                font-semibold
                                text-white
                                transition
                                hover:bg-[#172239]
                                disabled:cursor-not-allowed
                                disabled:opacity-50

                                max-sm:flex-1
                            "
            >
              {submitting ? (
                <>
                  <span
                    className="
                                            h-3.5
                                            w-3.5
                                            animate-spin
                                            rounded-full
                                            border-2
                                            border-white/30
                                            border-t-white
                                        "
                  />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
